import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { fmt } from '../api/services.api';
import { useCartStore } from '../../cart/store/cartStore';
import { useToast } from '../../../shared/components/Toast/ToastProvider';
import { BEAUTY_PACKAGE } from '../data/customFlows';

const PRIMARY = '#082189';

/* ─── Modal shell ─── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100010;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  @media (max-width: 480px) {
    padding: 0;
    align-items: flex-end;
  }
`;

const Card = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 480px) {
    max-height: 90vh;
    max-height: 90dvh;
    border-radius: 20px 20px 0 0;
  }
`;

const HeaderBand = styled.div`
  position: relative;
  flex-shrink: 0;
  background: #fff4ec;
  padding: 22px 20px 18px;
  border-bottom: 1px solid #ffe6d5;

  @media (max-width: 480px) {
    padding: 18px 16px 14px;
  }
`;

const Title = styled.h2`
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 900;
  color: #111827;
  letter-spacing: -0.5px;
  padding-right: 34px;

  @media (max-width: 480px) {
    font-size: 19px;
  }
`;

const CategoryLabel = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #b45309;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.8);
  color: #374151;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #ffffff; }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 6px 20px 12px;

  @media (max-width: 480px) {
    padding: 4px 16px 10px;
  }
`;

/* ─── Item row ─── */
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
`;

const Check = styled.button`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid ${p => (p.$checked ? PRIMARY : '#cbd5e1')};
  background: ${p => (p.$checked ? PRIMARY : '#ffffff')};
  color: #ffffff;
  font-size: 13px;
  font-weight: 900;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.div`
  font-size: 13.5px;
  font-weight: 800;
  color: #111827;
  line-height: 1.35;
`;

const PriceLine = styled.div`
  font-size: 13px;
  color: #111827;
  margin-top: 3px;
`;

const Mrp = styled.span`
  color: #9ca3af;
  text-decoration: line-through;
  font-size: 12px;
  margin-left: 6px;
`;

// Tappable chip that shows the current wax type — opens the radio picker below.
const VariantChip = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 160px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 12.5px;
  font-weight: 700;
  padding: 8px 12px;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover { border-color: ${PRIMARY}; }

  @media (max-width: 480px) {
    max-width: 130px;
    font-size: 12px;
    padding: 8px 10px;
  }
`;

const ChipLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipCaret = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  color: #9ca3af;
`;

/* ─── Nested variant picker modal ─── */
const PickerOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100020;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  @media (max-width: 480px) {
    padding: 0;
    align-items: flex-end;
  }
`;

const PickerCard = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 460px;
  max-height: 82vh;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);

  @media (max-width: 480px) {
    max-height: 82dvh;
    border-radius: 20px 20px 0 0;
  }
`;

const PickerHead = styled.div`
  position: relative;
  flex-shrink: 0;
  padding: 22px 22px 12px;
`;

const PickerTitle = styled.h3`
  margin: 0;
  font-size: 21px;
  font-weight: 900;
  color: #111827;
  letter-spacing: -0.4px;
  padding-right: 34px;
`;

const PickerBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 22px 20px;
`;

const PickerRow = styled.button`
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;

  &:last-child { border-bottom: none; }
`;

const PickerRadio = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${p => (p.$checked ? PRIMARY : '#cbd5e1')};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: ${p => (p.$checked ? PRIMARY : 'transparent')};
  }
`;

const PickerName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 500;
  color: #111827;
`;

const PickerPrice = styled.span`
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
`;

const SectionTitle = styled.h4`
  margin: 18px 0 2px;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
`;

const OptOutRow = styled.button`
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  text-align: left;
`;

const SectionDivider = styled.div`
  height: 8px;
  margin: 8px -20px 0;
  background: #ededed;

  @media (max-width: 480px) {
    margin: 8px -16px 0;
  }
`;

/* ─── Footer ─── */
const Footer = styled.div`
  position: sticky;
  bottom: 0;
  flex-shrink: 0;
  background: #ffffff;
  border-top: 1px solid #eef0f4;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.06);
`;

const SavingsStrip = styled.div`
  background: #ecfdf5;
  color: #047857;
  font-size: 13px;
  font-weight: 700;
  padding: 9px 20px;
  text-align: center;
`;

const FooterRow = styled.div`
  padding: 14px 20px;
  padding-bottom: max(14px, env(safe-area-inset-bottom));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  @media (max-width: 480px) {
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }
`;

const TotalCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const TotalRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const TotalValue = styled.span`
  font-size: 20px;
  font-weight: 900;
  color: #111827;
`;

const TotalMrp = styled.span`
  font-size: 13px;
  color: #9ca3af;
  text-decoration: line-through;
`;

const TotalLabel = styled.span`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
`;

const AddBtn = styled.button`
  flex-shrink: 0;
  border: none;
  cursor: pointer;
  background: ${p => (p.$disabled ? '#c7cbdb' : PRIMARY)};
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  padding: 12px 24px;
  border-radius: 10px;
  transition: opacity 0.15s, transform 0.12s;
  pointer-events: ${p => (p.$disabled ? 'none' : 'auto')};

  &:hover { opacity: 0.92; transform: scale(1.02); }

  @media (max-width: 480px) {
    padding: 12px 18px;
  }
`;

const MakeYourOwnPackageModal = ({ open, onClose, ctx = {}, pkg: pkgProp }) => {
  const navigate = useNavigate();
  const addItem = useCartStore(s => s.addItem);
  const toast = useToast();

  const pkg = pkgProp || BEAUTY_PACKAGE;
  const sections = pkg.sections || [];

  // Flat list of items (with a back-reference to their section).
  const allItems = useMemo(
    () => sections.flatMap(s => s.items.map(it => ({ ...it, section: s }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Variant lookup across every section that has wax-type variants.
  const variantById = useMemo(() => {
    const m = {};
    sections.forEach(s => (s.variants || []).forEach(v => { m[v.id] = v; }));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Item whose wax-type picker is open: { item, section } | null.
  const [variantPicker, setVariantPicker] = useState(null);

  // Set of checked item ids.
  const [checked, setChecked] = useState(() => new Set());
  // Selected variant per item id (default: item.defaultVariant → section's first).
  const [variants, setVariants] = useState(() => {
    const init = {};
    sections.forEach(s => s.items.forEach(it => {
      init[it.id] = it.defaultVariant || s.variants?.[0]?.id || null;
    }));
    return init;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  const effectivePrice = (it) => it.price + (variantById[variants[it.id]]?.delta ?? 0);

  const { savings, sumEffective, sumMrp } = useMemo(() => {
    let sav = 0, eff = 0, mrp = 0;
    allItems.forEach(it => {
      if (checked.has(it.id)) {
        const ep = effectivePrice(it);
        eff += ep;
        mrp += it.mrp ?? ep;
        sav += (it.mrp ?? ep) - ep;
      }
    });
    return { savings: sav, sumEffective: eff, sumMrp: mrp };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, variants]);

  if (!open) return null;

  const toggle = (id) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // "I don't need X" — clears all selections in that section.
  const clearSection = (section) => {
    setChecked(prev => {
      const next = new Set(prev);
      section.items.forEach(it => next.delete(it.id));
      return next;
    });
  };
  const sectionHasSelection = (section) => section.items.some(it => checked.has(it.id));

  const nothingChecked = checked.size === 0;

  const handleAdd = () => {
    if (nothingChecked) return;
    allItems.forEach(it => {
      if (!checked.has(it.id)) return;
      const variant = variantById[variants[it.id]];
      addItem({
        subServiceID: variant ? `pkg-${it.id}-${variant.id}` : `pkg-${it.id}`,
        subServiceName: variant ? `${it.name} (${variant.label})` : it.name,
        quantity: 1,
        price: effectivePrice(it),
        mrp: it.mrp ?? effectivePrice(it),
        cgst: 0,
        sgst: 0,
        imageURL: '',
        serviceID: ctx.serviceID,
        subCategoryID: ctx.subCategoryID,
        categoryName: ctx.categoryName || 'Glam & Glow',
        categorySlug: ctx.categorySlug || 'glam-glow',
        packageID: 1,
      });
    });
    toast.success('Added to cart');
    onClose?.();
    navigate('/cart');
  };

  return (
    <>
    <Overlay onClick={onClose}>
      <Card onClick={e => e.stopPropagation()}>
        <HeaderBand>
          <CloseBtn onClick={onClose} aria-label="Close">×</CloseBtn>
          <Title>{pkg.title}</Title>
          <CategoryLabel>{pkg.meta}</CategoryLabel>
        </HeaderBand>

        <Body>
          {sections.map((section, si) => (
            <React.Fragment key={section.id}>
              <SectionTitle>{section.title}</SectionTitle>
              {section.items.map(it => {
                const isChecked = checked.has(it.id);
                const ep = effectivePrice(it);
                return (
                  <Row key={it.id}>
                    <Check
                      $checked={isChecked}
                      onClick={() => toggle(it.id)}
                      aria-label={isChecked ? 'Remove' : 'Add'}
                    >
                      {isChecked ? '✓' : ''}
                    </Check>
                    <Info>
                      <ItemName>{it.name}</ItemName>
                      <PriceLine>
                        <strong>{fmt(ep)}</strong>
                        {it.mrp && it.mrp > ep ? <Mrp>{fmt(it.mrp)}</Mrp> : null}
                      </PriceLine>
                    </Info>
                    {section.variants ? (
                      <VariantChip
                        onClick={() => setVariantPicker({ item: it, section })}
                        aria-label="Choose wax type"
                      >
                        <ChipLabel>{variantById[variants[it.id]]?.label ?? 'Select'}</ChipLabel>
                        <ChipCaret>▾</ChipCaret>
                      </VariantChip>
                    ) : null}
                  </Row>
                );
              })}
              {section.optOut ? (
                <OptOutRow onClick={() => clearSection(section)}>
                  <Check $checked={!sectionHasSelection(section)} as="span">
                    {!sectionHasSelection(section) ? '✓' : ''}
                  </Check>
                  <ItemName>{section.optOut}</ItemName>
                </OptOutRow>
              ) : null}
              {si < sections.length - 1 ? <SectionDivider /> : null}
            </React.Fragment>
          ))}
        </Body>

        <Footer>
          <SavingsStrip>
            🏷 You are saving {fmt(savings)} in this package
          </SavingsStrip>
          <FooterRow>
            <TotalCol>
              <TotalLabel>Total</TotalLabel>
              <TotalRow>
                <TotalValue>{fmt(sumEffective)}</TotalValue>
                {sumMrp > sumEffective ? <TotalMrp>{fmt(sumMrp)}</TotalMrp> : null}
              </TotalRow>
            </TotalCol>
            <AddBtn $disabled={nothingChecked} onClick={handleAdd}>
              Add to cart
            </AddBtn>
          </FooterRow>
        </Footer>
      </Card>
    </Overlay>

    {variantPicker ? (
      <PickerOverlay onClick={() => setVariantPicker(null)}>
        <PickerCard onClick={e => e.stopPropagation()}>
          <PickerHead>
            <CloseBtn onClick={() => setVariantPicker(null)} aria-label="Close">×</CloseBtn>
            <PickerTitle>Choose the type of {variantPicker.section.title}</PickerTitle>
          </PickerHead>
          <PickerBody>
            {(variantPicker.section.variants || []).map(v => {
              const isSel = variants[variantPicker.item.id] === v.id;
              const price = variantPicker.item.price + (v.delta ?? 0);
              return (
                <PickerRow
                  key={v.id}
                  onClick={() => {
                    setVariants(prev => ({ ...prev, [variantPicker.item.id]: v.id }));
                    setVariantPicker(null);
                  }}
                >
                  <PickerRadio $checked={isSel} />
                  <PickerName>{v.label}</PickerName>
                  <PickerPrice>{fmt(price)}</PickerPrice>
                </PickerRow>
              );
            })}
          </PickerBody>
        </PickerCard>
      </PickerOverlay>
    ) : null}
    </>
  );
};

export default MakeYourOwnPackageModal;
